import {FilterAndData} from "../../api/filter-list";
import {Params} from "@angular/router";
import {GeneralSearchFilter} from "../../basic-entity-back/filters/general-search-filter";
import {RouteFilterTranslator} from "./route-filter-translator";

/**
 * Dos filtros son el mismo si son del mismo tipo, sobre la misma propiedad y con el mismo valor. El
 * valor se compara por su texto: el que se reconstruye al leer la URL es otro objeto, y el final del
 * día vuelve sin los milisegundos, que el texto de moment no enseña.
 */
function mismoFiltro(a: FilterAndData, b: FilterAndData): boolean {
    return a.filter === b.filter
        && (a.property?.modelKey ?? null) === (b.property?.modelKey ?? null)
        && String(a.data) === String(b.data);
}

/**
 * El botón cuyos filtros lleva la URL, para marcarlo aunque no se haya pulsado en esta visita (al
 * volver de una edición o al recargar). Si casan varios, el que más filtros aporta.
 */
export function botonAplicado<B extends { filter: FilterAndData[] }>(botones: B[], filtros: FilterAndData[]): B | null {
    const aplicados = botones.filter(boton =>
        boton.filter.length > 0 && boton.filter.every(f => filtros.some(u => mismoFiltro(f, u))));
    return aplicados.sort((a, b) => b.filter.length - a.filter.length)[0] || null;
}

/** Los filtros de la URL que no son del botón: los que se mantienen al pulsar otro botón */
export function sinFiltrosDeBoton(filtros: FilterAndData[], boton: { filter: FilterAndData[] } | null): FilterAndData[] {
    if (!boton) {
        return filtros;
    }
    return filtros.filter(u => !boton.filter.some(f => mismoFiltro(f, u)));
}

/**
 * Si el embudo del panel de filtros va en rojo: hay algún filtro aplicado que no es el de un botón.
 * El de un botón no cuenta porque pulsarlo nunca lo encendía.
 */
export function hayFiltrosDelPanel<B extends { filter: FilterAndData[] }>(filtros: FilterAndData[], botones: B[]): boolean {
    return sinFiltrosDeBoton(filtros, botonAplicado(botones, filtros)).length > 0;
}

const esBusqueda = (f: FilterAndData) => f.filter === GeneralSearchFilter;

/**
 * Lo que va a la URL al pulsar un botón (o TODOS, con `null`) en una tabla con panel: lo que hubiera menos el
 * botón anterior, más el nuevo. El panel y la búsqueda se conservan.
 */
export function filtrosAlPulsarBoton<B extends { filter: FilterAndData[] }>(filtrosUrl: FilterAndData[], botones: B[],
                                                                           filtroBoton: FilterAndData[] | null): FilterAndData[] {
    return [...sinFiltrosDeBoton(filtrosUrl, botonAplicado(botones, filtrosUrl)), ...(filtroBoton || [])];
}

/** Lo que va a la URL con una búsqueda general nueva (o sin ella, si viene vacía): el resto se conserva */
export function filtrosConBusqueda(filtrosUrl: FilterAndData[], texto: string): FilterAndData[] {
    const resto = filtrosUrl.filter(f => !esBusqueda(f));
    return texto ? [...resto, new FilterAndData(GeneralSearchFilter, texto)] : resto;
}

/** Los parámetros de la URL para esta lista de filtros (cada uno con su id) */
export function paramsDeFiltros(filtros: FilterAndData[]): Params {
    const porId: { [id: string]: FilterAndData } = {};
    filtros.forEach((filtro, i) => porId[i] = filtro);
    return RouteFilterTranslator.sFiltersToParams(porId);
}

/**
 * Al llegar a una tabla con panel: los filtros a los que hay que ir, o null para quedarse con la URL. Solo se
 * va a los valores por defecto si la URL no trae ningún filtro (ni del panel, ni de un botón, ni la búsqueda)
 * y no lleva `cleared`, que es la marca de que el usuario lo ha quitado todo en esta visita.
 */
export function filtrosAlAbrir(filtrosUrl: FilterAndData[], limpiado: boolean, porDefecto: FilterAndData[]): FilterAndData[] | null {
    if (limpiado || filtrosUrl.length > 0 || porDefecto.length === 0) {
        return null;
    }
    return porDefecto;
}

/**
 * Si la tabla tiene un botón que se aplica solo al abrirla sin filtros (Pedidos: REALIZADOS HOY). Entonces los
 * valores por defecto del panel no se aplican: manda el botón, y aplicar los dos a la vez daría dos navegaciones.
 */
export function hayBotonPorDefecto<B extends { default?: boolean }>(botones: B[] | null): boolean {
    return (botones || []).some(b => !!b.default);
}
